import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Flame, Trophy, Sparkles, Zap, ChevronRight, Loader2, Megaphone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trashStatus, trashAnnouncement, trashMissions, cents } from "@/lib/trashApi";

export default function TrashHome() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [ann, setAnn] = useState(null);
  const [missions, setMissions] = useState(null);

  useEffect(() => {
    trashAnnouncement().then((r) => setAnn(r.announcement)).catch(() => {});
    if (!user) return;
    trashStatus().then(setStatus).catch(() => {});
    trashMissions().then(setMissions).catch(() => {});
  }, [user]);

  if (loading) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  if (!user) {
    return (
      <div className="py-8 space-y-4" data-testid="trash-home-signedout">
        <div className="rounded-3xl border-2 border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/20 to-transparent p-6 text-center">
          <div className="font-black text-3xl">Sign in to start cleaning</div>
          <div className="text-white/70 text-sm mt-2">Track XP, missions, streaks, and earn real rewards.</div>
          <div className="flex gap-2 justify-center mt-5">
            <Link to="/login?next=/trashhunter" data-testid="home-signin"
              className="inline-flex items-center gap-1.5 bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full">Sign in</Link>
            <Link to="/register?next=/trashhunter" data-testid="home-register"
              className="inline-flex items-center gap-1.5 border border-white/30 bg-white/5 font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full">Sign up</Link>
          </div>
        </div>
        <Link to="/trashhunter/landing" className="block text-center text-xs text-white/50 underline mt-2">Learn more →</Link>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="trash-home">
      {ann?.active && (
        <div data-testid="trash-banner"
          className="rounded-2xl border p-3 flex items-start gap-3"
          style={{ background: annBg(ann.color), borderColor: annBorder(ann.color) }}>
          <Megaphone className="w-4 h-4 mt-0.5" style={{ color: annText(ann.color) }} />
          <div className="text-sm flex-1" style={{ color: annText(ann.color) }}>{ann.text}</div>
        </div>
      )}

      {status && (
        <section className="rounded-3xl border-2 border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/15 to-black/40 p-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#39FF14]/20 border-2 border-[#39FF14]/40 flex flex-col items-center justify-center">
              <div className="text-[9px] uppercase tracking-widest text-[#39FF14]">Level</div>
              <div className="font-black text-2xl leading-none">{status.level}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-lg truncate">{status.level_title}</div>
              <div className="text-xs text-white/60">{status.xp_into_level} / {status.xp_needed_next} XP to next level</div>
              <div className="mt-1.5 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#39FF14]" style={{ width: `${Math.min(100, (status.xp_into_level / status.xp_needed_next) * 100)}%` }} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-4">
            <Stat label="Coins" value={status.coins.toLocaleString()} accent="#00F0FF" />
            <Stat label="Wallet" value="Jixxeo" accent="#39FF14" />
            <Stat label="Cleanups" value={status.cleanups_total.toLocaleString()} accent="#FF00E5" />
            <Stat label="Streak" value={<span className="inline-flex items-center gap-0.5"><Flame className="w-3 h-3" />{status.streak_day}d</span>} accent="#FFD700" />
          </div>
        </section>
      )}

      <Link to="/trashhunter/scan" data-testid="home-scan-cta"
        className="block rounded-3xl border-2 border-[#39FF14]/50 bg-[#39FF14] text-black p-5 shadow-[0_0_35px_rgba(57,255,20,0.4)]">
        <div className="flex items-center gap-3">
          <Camera className="w-8 h-8" />
          <div className="flex-1">
            <div className="font-black text-lg leading-none">Start a cleanup</div>
            <div className="text-xs mt-1">Camera-only · GPS verified · earn XP + Coins</div>
          </div>
          <ChevronRight className="w-6 h-6" />
        </div>
      </Link>

      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="font-black text-sm uppercase tracking-widest text-white/80">Today's missions</div>
          <Link to="/trashhunter/missions" className="text-[10px] mono uppercase tracking-widest text-[#39FF14]">See all →</Link>
        </div>
        {!missions ? <div className="text-white/40 text-xs">Loading…</div> : (
          <div className="space-y-2">
            {missions.daily.slice(0, 3).map((m) => (
              <MissionRow key={m.key} m={m} />
            ))}
          </div>
        )}
      </section>

      <section className="grid grid-cols-2 gap-3">
        <Link to="/trashhunter/leaderboard" data-testid="home-leaderboard"
          className="rounded-2xl border border-white/10 bg-black/40 p-4 hover:border-[#39FF14]/40">
          <Trophy className="w-6 h-6 text-[#FFD700] mb-2" />
          <div className="font-black text-sm">Leaderboards</div>
          <div className="text-xs text-white/50 mt-0.5">Global · city · today · week</div>
        </Link>
        <Link to="/trashhunter/wallet" data-testid="home-wallet"
          className="rounded-2xl border border-white/10 bg-black/40 p-4 hover:border-[#39FF14]/40">
          <Sparkles className="w-6 h-6 text-[#39FF14] mb-2" />
          <div className="font-black text-sm">Wallet + Cash Out</div>
          <div className="text-xs text-white/50 mt-0.5">$5 min · $0.10 fee</div>
        </Link>
      </section>

      <section className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-4 text-xs">
        <div className="font-black text-[#FFD700] uppercase tracking-widest text-[10px] mb-1"><Zap className="w-3 h-3 inline" /> Elite membership</div>
        <div className="text-white/70">$10.99/mo — 2× XP, 1.25× coin bonuses, exclusive badges, and early access to new features.</div>
        <Link to="/trashhunter/me" className="inline-block mt-2 mono text-[10px] uppercase tracking-widest text-[#FFD700]">Manage in profile →</Link>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-2 text-center">
      <div className="text-[8px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-black text-sm mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function MissionRow({ m }) {
  const pct = Math.min(100, (m.progress / m.goal) * 100);
  return (
    <div data-testid={`home-mission-${m.key}`}
      className={"rounded-2xl border p-3 flex items-center gap-3 " +
        (m.completed ? "border-[#39FF14]/40 bg-[#39FF14]/10" : "border-white/10 bg-black/40")}>
      <div className="flex-1 min-w-0">
        <div className="font-black text-sm">{m.title}</div>
        <div className="text-xs text-white/60">{m.description}</div>
        <div className="mt-1.5 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#39FF14]" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="text-right">
        <div className="font-black text-sm tabular-nums">{m.progress}/{m.goal}</div>
        <div className="text-[9px] mono uppercase tracking-widest text-[#39FF14]">+{m.reward_xp} XP · +{m.reward_coins} c</div>
      </div>
    </div>
  );
}

function annBg(c) { return c === "gold" ? "rgba(255,215,0,0.10)" : c === "cyan" ? "rgba(0,240,255,0.10)" : c === "magenta" ? "rgba(255,0,229,0.10)" : c === "red" ? "rgba(248,113,113,0.10)" : "rgba(57,255,20,0.10)"; }
function annBorder(c) { return c === "gold" ? "rgba(255,215,0,0.35)" : c === "cyan" ? "rgba(0,240,255,0.35)" : c === "magenta" ? "rgba(255,0,229,0.35)" : c === "red" ? "rgba(248,113,113,0.35)" : "rgba(57,255,20,0.35)"; }
function annText(c) { return c === "gold" ? "#FFD700" : c === "cyan" ? "#00F0FF" : c === "magenta" ? "#FF00E5" : c === "red" ? "#F87171" : "#39FF14"; }
